/** Business facts used across legal pages, footers, payments, and customer-facing UI. */
export const BUSINESS = {
  brandName: 'Lame Assets',
  legalName: 'MOHAMMED MOINUDDIN',
  tagline: 'Gadgets & accessories · India',
  addressLine: '#48/1, 10th Main Road, 1st Stage, 3rd Block, HBR Layout, Bengaluru, Karnataka 560043, India',
  email: 'updates@lameassets.com',
  phone: '+91 81473 77100',
  phoneRaw: '8147377100',
  freeShippingAbove: 500,
  lastUpdated: '7 October 2026',
  get mailtoHref() {
    return `mailto:${this.email}`;
  },
  get telHref() {
    return `tel:+91${this.phoneRaw}`;
  },
};

/**
 * Suggested return rules for phone cases / accessories (hygiene + fit products).
 * Adjust later if needed — these are reasonable defaults for India e‑commerce.
 */
export const LEGAL_PAGES = {
  about: {
    slug: 'about',
    title: 'About Us',
    sections: [
      {
        heading: 'Who we are',
        paragraphs: [
          `${BUSINESS.brandName} is an India-based online store for phone cases and mobile accessories. We sell through our website and accept payments securely via Razorpay.`,
          `The store is operated by ${BUSINESS.legalName} (sole proprietor), based in Bengaluru, Karnataka.`,
        ],
      },
      {
        heading: 'What we sell',
        paragraphs: [
          'We focus on protective and expressive cases and related accessories for popular phone models. Product details, pricing, and availability are shown on each product page.',
        ],
      },
      {
        heading: 'Our promise',
        paragraphs: [
          'We aim for clear product information, reliable delivery within India, and responsive support if something goes wrong with your order.',
          `Questions? Email ${BUSINESS.email} or call ${BUSINESS.phone}.`,
        ],
      },
    ],
  },

  contact: {
    slug: 'contact',
    title: 'Contact Us',
    sections: [
      {
        heading: 'Support',
        paragraphs: [
          'We typically reply within 24–48 hours on business days.',
        ],
        contacts: [
          {
            label: 'Email',
            value: BUSINESS.email,
            href: BUSINESS.mailtoHref,
          },
          {
            label: 'Phone / WhatsApp',
            value: BUSINESS.phone,
            href: BUSINESS.telHref,
          },
        ],
      },
      {
        heading: 'Business address',
        paragraphs: [
          BUSINESS.addressLine,
        ],
      },
      {
        heading: 'Legal entity',
        paragraphs: [
          `${BUSINESS.brandName} is operated by ${BUSINESS.legalName} (sole proprietor).`,
        ],
      },
    ],
  },

  terms: {
    slug: 'terms',
    title: 'Terms & Conditions',
    sections: [
      {
        heading: '1. Agreement',
        paragraphs: [
          `By using ${BUSINESS.brandName} (the “Website”) or placing an order, you agree to these Terms & Conditions. If you do not agree, please do not use the Website.`,
          `The Website is operated by ${BUSINESS.legalName} (“we”, “us”, “our”).`,
        ],
      },
      {
        heading: '2. Eligibility',
        paragraphs: [
          'You must be at least 18 years old, or use the Website under the supervision of a parent/guardian, and be capable of entering a binding contract under Indian law.',
        ],
      },
      {
        heading: '3. Products & pricing',
        paragraphs: [
          'Product descriptions, images, and prices are shown on the Website. We try to keep information accurate, but minor variations (colour, packaging) may occur.',
          'Prices are in Indian Rupees (INR). We may change prices at any time; the price charged is the one shown at checkout when you place the order.',
        ],
      },
      {
        heading: '4. Orders',
        paragraphs: [
          'Placing an order is an offer to buy. We may accept or decline an order (for example due to stock, pricing error, or suspected fraud). You will receive confirmation by email/SMS where available.',
          'Ensure your delivery address and contact details are correct. We are not responsible for delays or failed delivery caused by incorrect information you provide.',
        ],
      },
      {
        heading: '5. Payments',
        paragraphs: [
          'Payments are processed by Razorpay. We do not store your full card or UPI credentials on our servers.',
          'An order is considered paid only after successful payment confirmation from Razorpay.',
        ],
      },
      {
        heading: '6. Shipping & delivery',
        paragraphs: [
          'We currently ship within India only. Shipping charges, free-shipping thresholds, and timelines are described in our Shipping Policy.',
        ],
      },
      {
        heading: '7. Returns & refunds',
        paragraphs: [
          'Returns, cancellations, and refunds are governed by our Cancellation & Refund Policy.',
        ],
      },
      {
        heading: '8. User accounts',
        paragraphs: [
          'You are responsible for keeping your login credentials confidential and for activity under your account. Tell us immediately if you suspect unauthorised use.',
        ],
      },
      {
        heading: '9. Intellectual property',
        paragraphs: [
          `All content on the Website (logos, text, images, design) is owned by or licensed to ${BUSINESS.brandName}. You may not copy or reuse it without our written permission.`,
        ],
      },
      {
        heading: '10. Limitation of liability',
        paragraphs: [
          'To the extent permitted by law, we are not liable for indirect, incidental, or consequential losses arising from use of the Website or products.',
          'Our total liability for any order is limited to the amount you paid for that order.',
        ],
      },
      {
        heading: '11. Governing law',
        paragraphs: [
          'These Terms are governed by the laws of India. Courts in Bengaluru, Karnataka shall have exclusive jurisdiction, subject to applicable consumer protection laws.',
        ],
      },
      {
        heading: '12. Contact',
        paragraphs: [
          `For questions about these Terms: ${BUSINESS.email} | ${BUSINESS.phone}`,
          `Address: ${BUSINESS.addressLine}`,
        ],
      },
    ],
  },

  privacy: {
    slug: 'privacy',
    title: 'Privacy Policy',
    sections: [
      {
        heading: '1. Introduction',
        paragraphs: [
          `This Privacy Policy explains how ${BUSINESS.brandName} (“we”, “us”) collects, uses, and protects your information when you use our Website or place an order.`,
        ],
      },
      {
        heading: '2. Information we collect',
        bullets: [
          'Identity & contact: name, phone number, email address',
          'Delivery: shipping address and related order notes',
          'Account: login identifiers and preferences (if you create an account)',
          'Order & payment metadata: order ID, amount, payment status (card/UPI details are handled by Razorpay)',
          'Technical: device/browser type, IP address, and basic usage logs needed to run and secure the site',
        ],
      },
      {
        heading: '3. How we use your information',
        bullets: [
          'Process and fulfil orders',
          'Send OTPs, order updates, and support replies',
          'Arrange delivery through our logistics partners',
          'Prevent fraud and improve site reliability',
          'Comply with legal and tax obligations',
        ],
      },
      {
        heading: '4. Sharing with service providers',
        paragraphs: [
          'We share only what is needed to run the business:',
        ],
        bullets: [
          'Razorpay — payment processing',
          'Shiprocket / courier partners — delivery and tracking',
          'Email/SMS providers — transactional messages (OTP, order updates)',
        ],
        paragraphsAfter: [
          'We do not sell your personal information.',
        ],
      },
      {
        heading: '5. Data retention',
        paragraphs: [
          'We keep order and account records as long as needed for support, accounting, and legal requirements, then delete or anonymise them where practical.',
        ],
      },
      {
        heading: '6. Security',
        paragraphs: [
          'We use reasonable technical and organisational measures to protect your data. No method of transmission over the internet is 100% secure.',
        ],
      },
      {
        heading: '7. Your choices',
        paragraphs: [
          `You may request access, correction, or deletion of your personal data (subject to legal retention needs) by emailing ${BUSINESS.email}.`,
        ],
      },
      {
        heading: '8. Contact',
        paragraphs: [
          `Privacy requests: ${BUSINESS.email} | ${BUSINESS.phone}`,
          `Address: ${BUSINESS.addressLine}`,
        ],
      },
    ],
  },

  refund: {
    slug: 'refund-policy',
    title: 'Cancellation & Refund Policy',
    sections: [
      {
        heading: 'Overview',
        paragraphs: [
          `Phone cases and accessories are personal-fit products. To keep things fair for buyers and sellers, ${BUSINESS.brandName} follows the rules below.`,
        ],
      },
      {
        heading: '1. Cancellation before dispatch',
        paragraphs: [
          'You may cancel an order before it is packed/dispatched. Contact us as soon as possible with your order ID.',
          'If payment was successful and we cancel before dispatch, we will issue a full refund to the original payment method.',
        ],
      },
      {
        heading: '2. Returns after delivery (7 days)',
        paragraphs: [
          'You may request a return within 7 days of delivery if all of the following are true:',
        ],
        bullets: [
          'The item is in good condition (used or fitted on a phone is fine, as long as it is not heavily worn or damaged)',
          'It is returned with original packaging where possible',
          'It does not have cracks, deep scratches, stains, liquid damage, or broken parts from misuse',
          'You share clear photos and your order ID when requesting the return',
        ],
      },
      {
        heading: '3. Defective, damaged, or wrong item',
        paragraphs: [
          'If you receive a damaged, defective, or incorrect product, tell us within 48 hours of delivery with photos/video of the issue and packaging.',
          'Approved cases: we will arrange a replacement (subject to stock) or a full refund, including outbound shipping where applicable.',
        ],
      },
      {
        heading: '4. Non-returnable',
        bullets: [
          'Items that are heavily damaged, cracked, stained, or otherwise not in good condition',
          'Cases ordered for the wrong model by mistake after delivery (unless we shipped the wrong model)',
          'Products marked as final sale / clearance (if stated on the product page)',
        ],
      },
      {
        heading: '5. Return shipping',
        paragraphs: [
          'Defective / wrong / damaged-in-transit items: we cover return shipping for approved claims.',
          'Change-of-mind returns that meet Section 2: return shipping may be deducted from the refund or paid by you, unless we state otherwise when approving the return.',
        ],
      },
      {
        heading: '6. Refund timeline',
        paragraphs: [
          'Once we receive and inspect the returned item (or approve a cancellation), refunds are initiated within 5–7 business days to the original payment method via Razorpay.',
          'Banks/UPI apps may take additional time to reflect the credit.',
        ],
      },
      {
        heading: '7. How to raise a request',
        paragraphs: [
          `Email ${BUSINESS.email} or call ${BUSINESS.phone} with: order ID, reason, and photos (for returns/defects).`,
        ],
      },
    ],
  },

  shipping: {
    slug: 'shipping-policy',
    title: 'Shipping & Delivery Policy',
    sections: [
      {
        heading: '1. Service area',
        paragraphs: [
          'We currently ship across India only. We do not offer international shipping.',
        ],
      },
      {
        heading: '2. Shipping charges',
        bullets: [
          `Free standard shipping on orders of ₹${BUSINESS.freeShippingAbove} and above (before applicable discounts, as shown at checkout)`,
          `Orders below ₹${BUSINESS.freeShippingAbove}: shipping charges (if any) are shown at checkout before you pay`,
        ],
      },
      {
        heading: '3. Processing & delivery time',
        paragraphs: [
          'Orders are usually processed within 1–2 business days after successful payment (excluding Sundays and public holidays).',
          'Delivery timelines depend on your pincode and courier serviceability. Typical doorstep delivery is about 3–7 business days after dispatch for most Indian pin codes. Estimated dates shown at checkout are indicative.',
        ],
      },
      {
        heading: '4. Tracking',
        paragraphs: [
          'Once your order is handed to the courier, we share AWB / tracking details where available (email, SMS, or in your Orders page).',
        ],
      },
      {
        heading: '5. Failed delivery / RTO',
        paragraphs: [
          'Please keep your phone reachable and provide accurate address landmarks. If a shipment returns to us as undelivered (RTO) due to incorrect address, unreachable customer, or refused delivery, we may refund the product amount after deducting outbound shipping and courier return costs, unless the failure was our error.',
        ],
      },
      {
        heading: '6. Contact',
        paragraphs: [
          `Shipping help: ${BUSINESS.email} | ${BUSINESS.phone}`,
          `Address: ${BUSINESS.addressLine}`,
        ],
      },
    ],
  },
};

export const LEGAL_ROUTE_ALIASES = {
  termsofuse: 'terms',
  privacypolicy: 'privacy',
  'terms-and-conditions': 'terms',
  'privacy-policy': 'privacy',
  'cancellation-refund-policy': 'refund',
  'refunds': 'refund',
  'shipping': 'shipping',
  'about-us': 'about',
  'contact-us': 'contact',
};

export function getLegalPage(key) {
  return LEGAL_PAGES[key] || null;
}
